/**
 * Validates one exposure section instance's answers on Continue (never while typing), with H2's
 * own messages: `04-05-GuidedExposures.dc.html`'s `errorsFor`, which H2's README calls accurate
 * for validation rules. Each error pairs the error summary's line, naming the question, with the
 * shorter message shown under the field.
 */
import { exposureContent } from "./exposure-content";
import {
  checkYearKey,
  detailTextKey,
  readCheckYear,
  readLimitsBlocks,
  readRepeatItems,
  readString,
  repeatAnswerKey,
  yesNoDetailAnswerKey,
  type ExposureAnswers,
  type RepeatItemAnswer,
} from "./exposure-answers";
import { getOnFileFieldSpec, type SectionOnFileSpec } from "./on-file-fields";
import { excludedEpicRecordIds, findOnFileScreen, soldEpicRecordIds, type OnFileRenderContext } from "./on-file-render";
import { isRepeatFieldShown } from "./exposure-schema";
import type { ExposureQuestion, ExposureSectionSchema, RepeatBlockSpec, RepeatFieldSpec, ShowRuleRow } from "./exposure-schema";
import type { SectionId } from "../../routing/route-table";

export interface ExposureErrorSummaryItem {
  readonly fieldId: string;
  readonly anchorId: string;
  /** The error summary's line, naming the question. */
  readonly text: string;
  /** The message shown under the field itself, when it differs from `text` (H2). */
  readonly fieldText?: string;
}

const YEAR_PATTERN = /^\d{4}$/;

function blank(value: string | undefined): boolean {
  return (value ?? "").trim() === "";
}

function fieldName(field: RepeatFieldSpec): string {
  return field.label.replace(/\s*\(optional\)\s*$/i, "");
}

function validateYesNoDetail(question: ExposureQuestion, answers: ExposureAnswers): ExposureErrorSummaryItem[] {
  const answer = readString(answers, yesNoDetailAnswerKey(question.fieldId));
  if (answer !== "yes" && answer !== "no") {
    return [
      {
        fieldId: question.fieldId,
        anchorId: question.fieldId,
        text: exposureContent.questionMissingAnswer(question.number),
        fieldText: exposureContent.fieldMissingAnswer,
      },
    ];
  }
  if (answer === "yes" && blank(readString(answers, detailTextKey(question.fieldId)))) {
    return [
      {
        fieldId: question.fieldId,
        anchorId: detailTextKey(question.fieldId),
        text: exposureContent.questionMissingDetail(question.number),
        fieldText: exposureContent.fieldMissingDetail,
      },
    ];
  }
  return [];
}

/** The picker context validation needs: the question's on-file spec and read, every row of the
 * question (for the cross-row exclusion) and the sold source's rows (for `soldAware`). */
export interface PickerContext {
  readonly onFileSpec: SectionOnFileSpec | undefined;
  readonly onFile: OnFileRenderContext | undefined;
  readonly rows: readonly RepeatItemAnswer[];
  readonly soldRows: readonly RepeatItemAnswer[];
}

/** H2 `optionsLeft`: whether a picker field has anything left for `row` to pick. A read still
 * loading or failed is never "nothing left" (AC-9, REQ-001), so the picker stays required. */
export function pickerHasNothingLeft(field: RepeatFieldSpec, row: RepeatItemAnswer, context: PickerContext): boolean {
  const pickerSpec = context.onFileSpec?.picker;
  if (field.newOption !== undefined || context.onFile?.state.status !== "succeeded") {
    return false;
  }
  if (pickerSpec === undefined || pickerSpec.screenKey === undefined) {
    return true;
  }
  const excluded = new Set(excludedEpicRecordIds(pickerSpec.screenKey, pickerSpec.fieldId, context.rows, row.key));
  const soldSource = context.onFileSpec?.soldSource;
  if (field.soldAware === true && soldSource !== undefined) {
    for (const id of soldEpicRecordIds(pickerSpec.screenKey, soldSource.pickerFieldId, context.soldRows, soldSource.soldOutcome)) {
      excluded.add(id);
    }
  }
  const screen = findOnFileScreen(context.onFile, pickerSpec.screenKey);
  return !(screen?.items ?? []).some((item) => item.epicRecordId !== null && !excluded.has(item.epicRecordId));
}

/** Whether a field is on screen for `row`: its show rules hold and, for a `hideUntilPicked`
 * field, the question's picker holds an on-file record. */
export function isFieldOnScreen(
  field: RepeatFieldSpec,
  row: ShowRuleRow & Partial<Pick<RepeatItemAnswer, "pickedOnFileItems">>,
  siblings: readonly RepeatFieldSpec[],
  pickerFieldId: string | undefined,
): boolean {
  if (field.hideUntilPicked === true) {
    const picked =
      pickerFieldId !== undefined && row.pickedOnFileItems !== undefined ? row.pickedOnFileItems[pickerFieldId] : undefined;
    if (picked === undefined) {
      return false;
    }
  }
  return isRepeatFieldShown(field, row, siblings);
}

interface FieldValidationTarget {
  readonly number: string;
  readonly questionFieldId: string;
  readonly where: string;
  /** The anchor of one field: `${anchorPrefix}${field.id}`. */
  readonly anchorPrefix: string;
}

function checkYearErrors(field: RepeatFieldSpec, row: RepeatItemAnswer, target: FieldValidationTarget): ExposureErrorSummaryItem[] {
  const ticked = readCheckYear(row, field.id);
  const anchor = `${target.anchorPrefix}${field.id}`;
  if (ticked.size === 0) {
    return [
      {
        fieldId: target.questionFieldId,
        anchorId: `${anchor}-${field.options?.[0]?.value ?? ""}`,
        text: exposureContent.questionNoSystem(target.number, target.where),
        fieldText: exposureContent.fieldNoSystem,
      },
    ];
  }
  const errors: ExposureErrorSummaryItem[] = [];
  for (const option of field.options ?? []) {
    const year = ticked.get(option.value);
    if (year === undefined) {
      continue;
    }
    const yearAnchor = `${target.anchorPrefix}${checkYearKey(field.id, option.value)}`;
    const base = { fieldId: target.questionFieldId, anchorId: yearAnchor };
    if (blank(year)) {
      errors.push({
        ...base,
        text: exposureContent.questionSystemYearMissing(target.number, option.label, target.where),
        fieldText: exposureContent.fieldSystemYearMissing,
      });
    } else if (!YEAR_PATTERN.test(year.trim())) {
      errors.push({
        ...base,
        text: exposureContent.questionSystemYearDigits(target.number, option.label, target.where),
        fieldText: exposureContent.fieldSystemYearDigits,
      });
    } else if (Number.parseInt(year, 10) > new Date().getFullYear()) {
      errors.push({
        ...base,
        text: exposureContent.questionSystemYearFuture(target.number, option.label, target.where),
        fieldText: exposureContent.fieldSystemYearFuture,
      });
    }
  }
  return errors;
}

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** H2: a typed date is checked as well as the calendar's own limit. */
function dateError(field: RepeatFieldSpec, value: string, target: FieldValidationTarget, anchorId: string): ExposureErrorSummaryItem | undefined {
  if (blank(value) || field.maxToday !== true) {
    return undefined;
  }
  const name = fieldName(field);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime());
  if (!valid) {
    return {
      fieldId: target.questionFieldId,
      anchorId,
      text: exposureContent.questionRealDate(target.number, name, target.where),
      fieldText: exposureContent.fieldRealDate(name),
    };
  }
  if (value > todayIso()) {
    return {
      fieldId: target.questionFieldId,
      anchorId,
      text: exposureContent.questionInPast(target.number, name, target.where),
      fieldText: exposureContent.fieldInPast(name),
    };
  }
  return undefined;
}

function yearError(field: RepeatFieldSpec, value: string, target: FieldValidationTarget, anchorId: string): ExposureErrorSummaryItem | undefined {
  if (field.type !== "year" || blank(value)) {
    return undefined;
  }
  const name = fieldName(field);
  if (!YEAR_PATTERN.test(value.trim())) {
    return {
      fieldId: target.questionFieldId,
      anchorId,
      text: exposureContent.questionYearDigits(target.number, name, target.where),
      fieldText: exposureContent.fieldYearDigits(name),
    };
  }
  if (Number.parseInt(value, 10) > new Date().getFullYear() && field.futureOk !== true) {
    return {
      fieldId: target.questionFieldId,
      anchorId,
      text: exposureContent.questionInPast(target.number, name, target.where),
      fieldText: exposureContent.fieldInPast(name),
    };
  }
  return undefined;
}

/** One shown field's errors, in H2's order of checks. */
export function fieldErrors(
  field: RepeatFieldSpec,
  row: RepeatItemAnswer,
  target: FieldValidationTarget,
  picker: PickerContext | undefined,
): ExposureErrorSummaryItem[] {
  const anchorId = `${target.anchorPrefix}${field.id}`;
  const value = row.fields[field.id] ?? "";
  switch (field.type) {
    case "static":
      return [];
    case "checkYear":
      return checkYearErrors(field, row, target);
    case "select":
    case "picker": {
      if (field.type === "picker" && picker !== undefined && pickerHasNothingLeft(field, row, picker)) {
        return [];
      }
      if (!blank(value)) {
        return [];
      }
      const emptyLabel = field.emptyLabel ?? exposureContent.defaultSelectEmptyLabel;
      return [
        {
          fieldId: target.questionFieldId,
          anchorId,
          text: exposureContent.questionSelectMissing(target.number, emptyLabel, target.where),
          fieldText: emptyLabel,
        },
      ];
    }
    case "radio":
    case "yesno":
      return blank(value)
        ? [
            {
              fieldId: target.questionFieldId,
              anchorId,
              text: exposureContent.questionRadioMissing(target.number, field.label, target.where),
              fieldText: exposureContent.fieldRadioMissing,
            },
          ]
        : [];
    default:
      break;
  }
  const formatError = dateError(field, value, target, anchorId) ?? yearError(field, value, target, anchorId);
  if (formatError !== undefined) {
    return [formatError];
  }
  if (blank(value) && field.defaultValue === undefined && field.optional !== true) {
    const name = fieldName(field);
    return [
      {
        fieldId: target.questionFieldId,
        anchorId,
        text: exposureContent.questionFieldMissing(target.number, name, target.where),
        fieldText: exposureContent.fieldFieldMissing(name),
      },
    ];
  }
  return [];
}

function repeatRowErrors(
  question: ExposureQuestion,
  repeat: RepeatBlockSpec,
  row: RepeatItemAnswer,
  rowNumber: number,
  picker: PickerContext,
): ExposureErrorSummaryItem[] {
  const where = exposureContent.repeatRowSuffix(repeat.itemLabel, rowNumber, repeat.single === true);
  const anchorPrefix = `${question.fieldId}-${row.key}-`;
  const errors: ExposureErrorSummaryItem[] = [];
  if (repeat.outcomeOptions !== undefined && blank(row.outcome)) {
    errors.push({
      fieldId: question.fieldId,
      anchorId: `${anchorPrefix}outcome`,
      text: exposureContent.questionRadioMissing(question.number, repeat.outcomeLegend ?? "", where),
      fieldText: exposureContent.fieldRadioMissing,
    });
  }
  const target = { number: question.number, questionFieldId: question.fieldId, where, anchorPrefix };
  const pickerFieldId = picker.onFileSpec?.picker?.fieldId;
  for (const field of repeat.fields) {
    if (isFieldOnScreen(field, row, repeat.fields, pickerFieldId)) {
      errors.push(...fieldErrors(field, row, target, picker));
    }
  }
  return errors;
}

/** The rows of the question a picker's `soldAware` reads (`on-file-fields.ts`'s `soldSource`). */
export function soldSourceRows(onFileSpec: SectionOnFileSpec | undefined, answers: ExposureAnswers): readonly RepeatItemAnswer[] {
  return onFileSpec?.soldSource === undefined ? [] : readRepeatItems(answers, onFileSpec.soldSource.fieldId);
}

function validateYesNoRepeat(
  question: ExposureQuestion,
  answers: ExposureAnswers,
  sectionId: SectionId,
  onFile: OnFileRenderContext | undefined,
): ExposureErrorSummaryItem[] {
  const repeat = question.repeat;
  if (repeat === undefined) {
    return [];
  }
  const answer = readString(answers, repeatAnswerKey(question.fieldId));
  if (answer !== "yes" && answer !== "no") {
    return [
      {
        fieldId: question.fieldId,
        anchorId: repeatAnswerKey(question.fieldId),
        text: exposureContent.questionMissingAnswer(question.number),
        fieldText: exposureContent.fieldMissingAnswer,
      },
    ];
  }
  if (answer === "no") {
    return [];
  }
  const rows = readRepeatItems(answers, question.fieldId);
  if (rows.length === 0) {
    return [
      {
        fieldId: question.fieldId,
        anchorId: question.fieldId,
        text: exposureContent.questionMissingRepeatItem(question.number),
      },
    ];
  }
  const onFileSpec = getOnFileFieldSpec(sectionId, question.fieldId);
  const picker: PickerContext = { onFileSpec, onFile, rows, soldRows: soldSourceRows(onFileSpec, answers) };
  return rows.flatMap((row, index) => repeatRowErrors(question, repeat, row, index + 1, picker));
}

/** Whether the limits question's property picker has a building left for block `blockKey`. */
export function limitsPropertyNothingLeft(
  onFileSpec: SectionOnFileSpec | undefined,
  onFile: OnFileRenderContext | undefined,
  answers: ExposureAnswers,
  fieldId: string,
  blockKey: string,
): boolean {
  if (onFile?.state.status !== "succeeded") {
    return false;
  }
  const pickerSpec = onFileSpec?.picker;
  const screen = findOnFileScreen(onFile, pickerSpec?.screenKey);
  const used = new Set(
    readLimitsBlocks(answers, fieldId)
      .filter((block) => block.key !== blockKey && block.property !== "")
      .map((block) => block.property),
  );
  const soldSource = onFileSpec?.soldSource;
  const sold =
    soldSource === undefined
      ? new Set<number>()
      : soldEpicRecordIds(pickerSpec?.screenKey, soldSource.pickerFieldId, soldSourceRows(onFileSpec, answers), soldSource.soldOutcome);
  return !(screen?.items ?? []).some(
    (item) => item.epicRecordId !== null && !sold.has(item.epicRecordId) && !used.has(String(item.epicRecordId)),
  );
}

function validateLimits(
  question: ExposureQuestion,
  answers: ExposureAnswers,
  sectionId: SectionId,
  onFile: OnFileRenderContext | undefined,
): ExposureErrorSummaryItem[] {
  const answer = readString(answers, repeatAnswerKey(question.fieldId));
  if (answer !== "yes" && answer !== "no") {
    return [
      {
        fieldId: question.fieldId,
        anchorId: repeatAnswerKey(question.fieldId),
        text: exposureContent.questionMissingAnswer(question.number),
        fieldText: exposureContent.fieldMissingAnswer,
      },
    ];
  }
  if (answer === "no") {
    return [];
  }
  const onFileSpec = getOnFileFieldSpec(sectionId, question.fieldId);
  const errors: ExposureErrorSummaryItem[] = [];
  for (const block of readLimitsBlocks(answers, question.fieldId)) {
    const anchor = `${question.fieldId}-${block.key}`;
    if (block.property === "") {
      // A property picker with nothing left is not the customer's error to fix (H2 `optionsLeft`).
      if (!limitsPropertyNothingLeft(onFileSpec, onFile, answers, question.fieldId, block.key)) {
        errors.push({
          fieldId: question.fieldId,
          anchorId: `${anchor}-property`,
          text: exposureContent.questionLimitsProperty(question.number),
          fieldText: exposureContent.fieldLimitsProperty,
        });
      }
      continue;
    }
    if (block.subjects.length === 0) {
      errors.push({
        fieldId: question.fieldId,
        anchorId: `${anchor}-subject`,
        text: exposureContent.questionLimitsNoSubject(question.number),
        fieldText: exposureContent.fieldLimitsNoSubject,
      });
      continue;
    }
    for (const subject of block.subjects) {
      if (Object.values(subject.values).every((value) => blank(value))) {
        errors.push({
          fieldId: question.fieldId,
          anchorId: `${anchor}-${subject.subject}-value`,
          text: exposureContent.questionLimitsValue(question.number, subject.label),
          fieldText: exposureContent.fieldLimitsValue,
        });
      }
    }
  }
  return errors;
}

/** Validates every question in `schema` against one section instance's `answers`. A `currency`
 * question needs a figure ("Question N: enter a figure"). The checklist has no required field in
 * H2, so it raises nothing.
 *
 * `onFile` is optional: without it every picker is validated as required; with a succeeded read,
 * a picker with nothing left to pick is not an error. */
export function validateExposureSection(
  schema: ExposureSectionSchema,
  answers: ExposureAnswers,
  onFile?: OnFileRenderContext,
): readonly ExposureErrorSummaryItem[] {
  const errors: ExposureErrorSummaryItem[] = [];
  for (const question of schema.questions) {
    switch (question.type) {
      case "yesno-detail":
        errors.push(...validateYesNoDetail(question, answers));
        break;
      case "yesno-repeat":
        errors.push(...validateYesNoRepeat(question, answers, schema.sectionId, onFile));
        break;
      case "limits":
        errors.push(...validateLimits(question, answers, schema.sectionId, onFile));
        break;
      case "currency":
        if (blank(readString(answers, question.fieldId))) {
          errors.push({
            fieldId: question.fieldId,
            anchorId: question.fieldId,
            text: exposureContent.questionMissingFigure(question.number),
            fieldText: exposureContent.fieldMissingFigure,
          });
        }
        break;
      default:
        break;
    }
  }
  return errors;
}

/** The row an additional-details page belongs to: the parent question, its row (whose change
 * type the page's show rules read) and the row's 1-based position in the question. */
export interface DetailsPageRow {
  readonly question: ExposureQuestion;
  readonly row: RepeatItemAnswer;
  readonly rowNumber: number;
}

/** The values a details page's show rules read: the parent row's change type, then the page's
 * own answers. */
export function detailsShowRuleRow(parent: DetailsPageRow, answers: ExposureAnswers): RepeatItemAnswer {
  const fields = Object.fromEntries(Object.entries(answers).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
  return { key: parent.row.key, outcome: parent.row.outcome, fields };
}

/** H2: an additional-details page is validated like its parent row ("Question 1: enter year
 * built for building 1"). Its fields' anchors are the field ids themselves. */
export function validateAdditionalDetailsPage(parent: DetailsPageRow, answers: ExposureAnswers): readonly ExposureErrorSummaryItem[] {
  const repeat = parent.question.repeat;
  const page = repeat?.additionalDetailsPage;
  if (repeat === undefined || page === undefined) {
    return [];
  }
  const row = detailsShowRuleRow(parent, answers);
  const target = {
    number: "1",
    questionFieldId: parent.question.fieldId,
    where: exposureContent.repeatRowSuffix(repeat.itemLabel, parent.rowNumber, false),
    anchorPrefix: "",
  };
  return page.fields
    .filter((field) => isRepeatFieldShown(field, row, page.fields))
    .flatMap((field) => fieldErrors(field, row, target, undefined));
}
